// Optimasi foto di browser sebelum diunggah: foto ≤3MB dikirim apa adanya
// (kualitas asli utuh, tidak disentuh). Foto >3MB dioptimalkan bertahap
// sampai ≤3MB dengan kualitas setinggi mungkin: sisi terpanjang 2000px
// JPEG 85% lebih dulu — perbedaan tak terlihat mata — lalu kualitas
// diturunkan sedikit demi sedikit, dan hanya bila perlu dimensinya
// diperkecil. GIF animasi tidak aman dikompresi (dikirim apa adanya).
// Semua tahap berbatas waktu & aman-gagal: bila kompresi gagal atau terlalu
// lambat, berkas ASLI dikembalikan (pemanggil yang memutuskan menolak).

const MAX_DIM = 2000;
const FALLBACK_DIM = 1200;
const MAX_BYTES = 3 * 1048576; // target: foto tersimpan maksimal 3MB
const QUALITY_STEPS = [0.85, 0.78, 0.7, 0.6];
const COMPRESS_TIMEOUT = 12000;

function withTimeout(promise, ms) {
  let t;
  const timer = new Promise((_, rej) => {
    t = setTimeout(() => rej(new Error("kompresi-lambat")), ms);
  });
  return Promise.race([promise, timer]).finally(() => clearTimeout(t));
}

function closeBitmap(bmp) {
  if (bmp && typeof bmp.close === "function") {
    try {
      bmp.close();
    } catch {
      /* diabaikan */
    }
  }
}

function loadViaImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("dekode-gagal"));
    };
    img.src = url;
  });
}

async function decode(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      /* coba cara lama di bawah */
    }
  }
  return loadViaImage(file);
}

async function renderBlob(src, bw, bh, quality) {
  return withTimeout(
    new Promise((res) => {
      const canvas = document.createElement("canvas");
      canvas.width = bw;
      canvas.height = bh;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(src, 0, 0, bw, bh);
      canvas.toBlob(res, "image/jpeg", quality);
    }),
    COMPRESS_TIMEOUT
  );
}

export async function compressImage(file) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  if (file.type === "image/gif") return file;
  if (file.size <= MAX_BYTES) return file; // sudah ≤3MB — kualitas asli utuh

  let bmp = null;
  try {
    bmp = await withTimeout(decode(file), COMPRESS_TIMEOUT);
    const w = bmp.width || bmp.naturalWidth;
    const h = bmp.height || bmp.naturalHeight;
    if (!w || !h) return file;

    for (const dim of [MAX_DIM, FALLBACK_DIM]) {
      const scale = Math.min(1, dim / Math.max(w, h));
      const bw = Math.max(1, Math.round(w * scale));
      const bh = Math.max(1, Math.round(h * scale));

      // Pengecilan native (hemat memori) bila didukung browser
      let src = bmp;
      if (bw < w && typeof createImageBitmap === "function" && bmp.width) {
        try {
          const resized = await withTimeout(
            createImageBitmap(bmp, {
              resizeWidth: bw,
              resizeHeight: bh,
              resizeQuality: "medium",
            }),
            COMPRESS_TIMEOUT
          );
          closeBitmap(bmp);
          src = bmp = resized;
        } catch {
          /* tanpa resize native — gambar langsung di canvas */
        }
      }

      for (const q of QUALITY_STEPS) {
        const blob = await renderBlob(src, bw, bh, q);
        if (blob && blob.size <= MAX_BYTES) {
          closeBitmap(bmp);
          bmp = null;
          return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.jpg`, {
            type: "image/jpeg",
          });
        }
      }
    }
    // tidak mampu mencapai 3MB — kembalikan asli, pemanggil yang menolak
    return file;
  } catch {
    closeBitmap(bmp);
    return file;
  }
}
